import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { BRANCH_META } from "@/lib/branches";
import { Sparkles, Award } from "lucide-react";

export default function LevelUpModal({ reward, onClose }) {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!reward) return;
        setOpen(true);
        const branch = reward.branch || "sciences";
        const color = BRANCH_META[branch]?.color || "#FFC107";
        // burst
        confetti({
            particleCount: 90,
            spread: 80,
            origin: { y: 0.4 },
            colors: [color, "#FFC107", "#FDFBF7", "#5AB2FF"],
        });
        const t = setTimeout(() => {
            confetti({
                particleCount: 60,
                spread: 100,
                origin: { y: 0.5 },
                colors: [color, "#FDFBF7"],
            });
        }, 350);
        return () => clearTimeout(t);
    }, [reward]);

    if (!reward) return null;

    const branch = reward.branch || "sciences";
    const meta = BRANCH_META[branch];

    const close = () => {
        setOpen(false);
        setTimeout(() => onClose && onClose(), 250);
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-md p-4"
                    onClick={close}
                    data-testid="level-up-modal"
                >
                    <motion.div
                        initial={{ scale: 0.6, rotateY: -30, opacity: 0 }}
                        animate={{ scale: 1, rotateY: 0, opacity: 1 }}
                        exit={{ scale: 0.7, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 220, damping: 18 }}
                        onClick={(e) => e.stopPropagation()}
                        className="crystal rounded-3xl max-w-md w-full p-8 text-center"
                        style={{ borderColor: meta.color }}
                    >
                        <div className="flex justify-center mb-4">
                            <div
                                className="w-20 h-20 rounded-full flex items-center justify-center animate-pulse-node"
                                style={{ background: meta.color, boxShadow: `0 0 40px ${meta.color}` }}
                            >
                                <Sparkles className="w-9 h-9 text-white" />
                            </div>
                        </div>

                        <p className="chip mb-2" style={{ background: meta.soft, color: meta.text }}>
                            {meta.name} branch expanded
                        </p>

                        {reward.leveled_up ? (
                            <>
                                <h2 className="font-display text-4xl font-black tracking-tighter text-slate-900">
                                    Level Up!
                                </h2>
                                <p className="font-display text-2xl font-bold text-slate-800 mt-1">
                                    Level {reward.old_level} → {reward.new_level}
                                </p>
                            </>
                        ) : (
                            <h2 className="font-display text-3xl font-black tracking-tighter text-slate-900">
                                +{reward.xp_gained} Epistemic Points
                            </h2>
                        )}

                        {reward.badge && (
                            <div
                                className="mt-5 rounded-2xl p-4 flex items-center gap-3 text-left"
                                style={{ background: meta.soft, border: `1.5px solid ${meta.color}` }}
                                data-testid="new-badge"
                            >
                                <Award className="w-7 h-7" style={{ color: meta.color }} />
                                <div>
                                    <p className="text-xs uppercase tracking-widest font-bold" style={{ color: meta.text }}>
                                        New merit badge unlocked
                                    </p>
                                    <p className="font-display font-bold text-lg text-slate-900">
                                        {reward.badge.name}
                                    </p>
                                </div>
                            </div>
                        )}

                        <button
                            onClick={close}
                            className="arcade-btn mt-6 w-full"
                            data-variant="primary"
                            data-testid="level-up-continue"
                        >
                            Continue exploring
                        </button>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
